import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import type { SupplierInvoiceStatus } from "@shared/finance/supplierInvoices";
import { LOCKED_HEADER_STATUSES } from "@shared/finance/supplierInvoices";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface Line {
  id: number;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  taxableBase: string;
  taxRate: string;
  vatAmount: string;
  totalAmount: string;
  position?: number | null;
}

interface SupplierInvoiceLinesProps {
  invoiceId: number;
  status: SupplierInvoiceStatus;
  lines: Line[];
  canWrite: boolean;
}

const EMPTY_LINE = {
  description: "",
  quantity: "1",
  unit: "ud",
  unitPrice: "0.00",
  taxableBase: "0.00",
  taxRate: "21.00",
  vatAmount: "0.00",
  totalAmount: "0.00",
};

function formatEUR(amount: string | number) {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

export function SupplierInvoiceLines({ invoiceId, status, lines, canWrite }: SupplierInvoiceLinesProps) {
  const utils = trpc.useUtils();
  const isLocked = LOCKED_HEADER_STATUSES.has(status);
  const canEdit = canWrite && !isLocked;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<Line | null>(null);
  const [form, setForm] = useState(EMPTY_LINE);

  const addLine = trpc.supplierInvoices.addLine.useMutation({
    onSuccess: () => {
      toast.success("Línea añadida");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      setDialogOpen(false);
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const updateLine = trpc.supplierInvoices.updateLine.useMutation({
    onSuccess: () => {
      toast.success("Línea actualizada");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
      setDialogOpen(false);
      setEditingLine(null);
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const deleteLine = trpc.supplierInvoices.deleteLine.useMutation({
    onSuccess: () => {
      toast.success("Línea eliminada");
      utils.supplierInvoices.getById.invalidate({ invoiceId });
    },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  function openAdd() {
    setEditingLine(null);
    setForm(EMPTY_LINE);
    setDialogOpen(true);
  }

  function openEdit(line: Line) {
    setEditingLine(line);
    setForm({
      description: line.description,
      quantity: line.quantity,
      unit: line.unit,
      unitPrice: line.unitPrice,
      taxableBase: line.taxableBase,
      taxRate: line.taxRate,
      vatAmount: line.vatAmount,
      totalAmount: line.totalAmount,
    });
    setDialogOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const lineData = {
      description: form.description,
      quantity: form.quantity,
      unit: form.unit,
      unitPrice: form.unitPrice,
      taxableBase: form.taxableBase,
      taxRate: form.taxRate,
      vatAmount: form.vatAmount,
      totalAmount: form.totalAmount,
    };
    if (editingLine) {
      updateLine.mutate({ lineId: editingLine.id, line: lineData });
    } else {
      addLine.mutate({ invoiceId, line: lineData });
    }
  }

  function set(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  const isPending = addLine.isPending || updateLine.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {lines.length} {lines.length === 1 ? "línea" : "líneas"}
        </span>
        {canEdit && (
          <Button size="sm" onClick={openAdd}>
            <Plus className="h-4 w-4 mr-1" />
            Añadir línea
          </Button>
        )}
      </div>

      {lines.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">Sin líneas registradas.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Descripción</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead>Ud.</TableHead>
                <TableHead className="text-right">P. unitario</TableHead>
                <TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right">IVA%</TableHead>
                <TableHead className="text-right">IVA €</TableHead>
                <TableHead className="text-right">Total</TableHead>
                {canEdit && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell>{line.description}</TableCell>
                  <TableCell className="text-right">{line.quantity}</TableCell>
                  <TableCell>{line.unit}</TableCell>
                  <TableCell className="text-right">{formatEUR(line.unitPrice)}</TableCell>
                  <TableCell className="text-right">{formatEUR(line.taxableBase)}</TableCell>
                  <TableCell className="text-right">{line.taxRate}%</TableCell>
                  <TableCell className="text-right">{formatEUR(line.vatAmount)}</TableCell>
                  <TableCell className="text-right font-medium">{formatEUR(line.totalAmount)}</TableCell>
                  {canEdit && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(line)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => deleteLine.mutate({ lineId: line.id })}
                          disabled={deleteLine.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLine ? "Editar línea" : "Añadir línea"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="line-desc">Descripción *</Label>
              <Input
                id="line-desc"
                value={form.description}
                onChange={set("description")}
                required
                minLength={1}
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="line-qty">Cantidad *</Label>
                <Input id="line-qty" type="number" step="0.001" min="0.001" value={form.quantity} onChange={set("quantity")} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="line-unit">Unidad</Label>
                <Input id="line-unit" value={form.unit} onChange={set("unit")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="line-unit-price">P. unitario</Label>
                <Input id="line-unit-price" type="number" step="0.01" min="0" value={form.unitPrice} onChange={set("unitPrice")} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="line-base">Base imponible *</Label>
                <Input id="line-base" type="number" step="0.01" min="0" value={form.taxableBase} onChange={set("taxableBase")} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="line-tax-rate">IVA %</Label>
                <Input id="line-tax-rate" type="number" step="0.01" min="0" max="100" value={form.taxRate} onChange={set("taxRate")} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="line-vat">IVA €</Label>
                <Input id="line-vat" type="number" step="0.01" min="0" value={form.vatAmount} onChange={set("vatAmount")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="line-total">Total *</Label>
                <Input id="line-total" type="number" step="0.01" min="0" value={form.totalAmount} onChange={set("totalAmount")} required />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Guardando..." : editingLine ? "Actualizar" : "Añadir"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
